import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Button, Card, FormGroup, JsonViewer, PageHeader } from "@cofano/cck";

import { Grid2 as Grid, CircularProgress } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import Helmet from "components/helmet/Helmet";

import { FormattedDate } from "utils/DateUtils";

import { CoreLogType } from "__generated__/enums";
import { GetCoreLogDocument } from "__generated__/view/corelogs/graphql/coreLog.queries.generated";

const CoreLogDetails = () => {

	const { id } = useParams();
	const navigate = useNavigate();

	const [ expand, setExpand ] = useState(true);

	const { data, loading } = useQuery(GetCoreLogDocument, {
		variables: {
			id: Number(id),
		},
	});

	const parseJson = (content: string) => {
		try {
			return [ JSON.parse(content), true ];
		} catch (error) {
			return [ content, false ];
		}
	};

	const download = () => {
		const blob = new Blob([ JSON.stringify(data?.coreLog) ], { type: "text/plain" });
		const url = URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.download = data?.coreLog?.id + ".json";
		link.href = url;

		document.body.appendChild(link);
		link.click();

		URL.revokeObjectURL(url);
	};

	const content = data?.coreLog?.content ?? "";

	const [ parsedJson, parsedSuccessfully ] = parseJson(content);

	const jsonEvent = {
		...data?.coreLog ?? {},
		content: parsedSuccessfully ? parsedJson : "**Invalid JSON**",
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{intl.formatMessage({ id: "CORE_LOG" })}
					<Helmet title={intl.formatMessage({ id: "CORE_LOG" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/corelogs")}>
						{intl.formatMessage({ id: "CORE_LOGS" })}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			{loading ? (
				<CircularProgress size={24} />
			) : (
				<Grid container spacing={3}>
					<Grid size={4}>
						<Card>
							<FormGroup label={intl.formatMessage({ id: "TYPE" })} labelPlacement="left-center" overflow="hidden">
								{data?.coreLog?.type}
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "EVENT_CLASS" })} labelPlacement="left-center" overflow="hidden">
								{data?.coreLog?.eventClass}
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "ENTITY_ID" })} labelPlacement="left-center" overflow="hidden">
								{data?.coreLog?.entityId}
							</FormGroup>

							{data?.coreLog?.type === CoreLogType.Out && (
								<>
									<FormGroup label={intl.formatMessage({ id: "TARGET_CUSTOMER" })} labelPlacement="left-center" overflow="hidden">
										{data?.coreLog?.targetCustomer}
									</FormGroup>

									<FormGroup label={intl.formatMessage({ id: "TARGET_ENVIRONMENT" })} labelPlacement="left-center" overflow="hidden">
										{data?.coreLog?.targetEnvironment}
									</FormGroup>

									<FormGroup label={intl.formatMessage({ id: "TARGET_APPLICATION" })} labelPlacement="left-center" overflow="hidden">
										{data?.coreLog?.targetApplication}
									</FormGroup>
								</>
							)}

							<FormGroup label={intl.formatMessage({ id: "TIMESTAMP" })} labelPlacement="left-center" overflow="hidden">
								<FormattedDate date={data?.coreLog?.timestamp} withoutYear />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "EXCEPTION_CLASS" })} labelPlacement="left-center" overflow="hidden">
								{data?.coreLog?.exceptionClass}
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "EXCEPTION_MESSAGE" })} labelPlacement="left" overflow="hidden">
								<div style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
									{data?.coreLog?.errorMessage}
								</div>
							</FormGroup>
						</Card>
					</Grid>

					{jsonEvent?.id && (
						<Grid size={8}>
							<Card
								actions={[ {
									icon: <RemoveIcon />,
									onClick: () => setExpand(false),
									hidden: !expand,
								}, {
									icon: <AddIcon />,
									onClick: () => setExpand(true),
									hidden: expand,
								}, {
									text: intl.formatMessage({ id: "DOWNLOAD" }),
									onClick: () => download(),
								} ]}>
								<JsonViewer data={jsonEvent} expand={expand} key={`${expand}`} />
							</Card>
						</Grid>
					)}
				</Grid>
			)}
		</div>
	);
};

export default CoreLogDetails;
